import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid2 from "@mui/material/Grid2";
import { Stack } from "@mui/system";

import LaunchIcon from "@mui/icons-material/Launch";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import { peopleAPI } from "api/Community/PeopleAPI";
import { addressesAPI } from "api/Community/AddressesAPI";
import { Person } from "api/Community/CommunityInterfaces";

import EditableFieldsTable from "components/Records/RecordDisplay/EditableFieldsTable";
import PersonDiscipleshipStageCard from "components/PersonDiscipleshipStageCard";
import GenericTabs, {
  GenericTabItem,
} from "components/Base/GenericTabs";
import ReligionMultiStageSelector from "pages/community/People/PeopleDetail/ReligionMultistageSelector";

import AddressPickerDialog from "components/Flow/Controls/AddressPickerDialog";
import { RoundIconButton } from "components/Base/Buttons";


const formatAddress = (a: any) => {
  if (!a) return "";

  const street = a.street_address || a.address || "";
  const city = a.city || "";
  const state = a.state || "";
  const post = a.post_code || "";
  const country = a.country || "";

  return [street, city, state, post, country]
    .filter(Boolean)
    .join(", ");
};


interface InlineSummaryProps<T extends { id?: number | string }> {
  record: T;
  titleFields: (keyof T)[];
  descriptionField?: keyof T;
  loading?: boolean;
  onUpdate: (
    id: T["id"],
    field: keyof T,
    value: any
  ) => Promise<void>;
}

function InlineSummary<T extends { id?: number | string }>({
  record,
  titleFields,
  loading,
  onUpdate,
}: InlineSummaryProps<T>) {
  const theme = useTheme();

  const [editingField, setEditingField] =
    useState<keyof T | null>(null);

  const [savingField, setSavingField] =
    useState<keyof T | null>(null);

  const [editValues, setEditValues] =
    useState<Partial<T>>({});

  const startEdit = (field: keyof T) => {
    setEditingField(field);

    setEditValues((previous) => ({
      ...previous,
      [field]: record[field],
    }));
  };

  const handleSave = async (field: keyof T) => {
    const newValue = editValues[field];

    if (
      newValue !== undefined &&
      newValue !== record[field]
    ) {
      try {
        setSavingField(field);

        await onUpdate(
          record.id!,
          field,
          newValue
        );
      } catch (error) {
        console.error(
          `Failed to update ${String(field)}:`,
          error
        );
      } finally {
        setSavingField(null);
      }
    }

    setEditingField(null);
  };

  const renderEditable = (
    field: keyof T,
    variant: "h3" | "body1" = "body1"
  ) => {
    const isEditing = editingField === field;

    const value = (
      editValues[field] ??
      record[field]
    ) as any;

    if (isEditing) {
      return (
        <TextField
          autoFocus
          variant="standard"
          fullWidth
          value={value ?? ""}
          onChange={(event) =>
            setEditValues((previous) => ({
              ...previous,
              [field]: event.target.value,
            }))
          }
          onBlur={() => handleSave(field)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSave(field);
            }

            if (event.key === "Escape") {
              setEditingField(null);
            }
          }}
          sx={{
            "& .MuiInputBase-input": {
              ...(variant === "h3"
                ? theme.typography.h3
                : theme.typography.body1),
              padding: 0,
            },
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant={variant}
          onClick={() => startEdit(field)}
          sx={{
            cursor: "text",
            borderRadius: 1,
            px: 0.5,

            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          {value || (
            <Box
              component="span"
              sx={{
                color: "text.secondary",
              }}
            >
              Click to edit…
            </Box>
          )}
        </Typography>

        {savingField === field && (
          <CircularProgress size={16} />
        )}
      </Box>
    );
  };

  return (
    <>
      <Grid2 container>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {titleFields.map((field) => (
              <React.Fragment key={String(field)}>
                {renderEditable(field, "h3")}
              </React.Fragment>
            ))}
          </Box>
        </Grid2>
      </Grid2>

      {loading && (
        <Box sx={{ mt: 2 }}>
          <CircularProgress size={18} />
        </Box>
      )}
    </>
  );
}


interface PeopleDetailProps {
  person: Person | null;
  interactionToken?: string;
  onPersonUpdated?: (updated: Person) => void;
}

const PeopleDetail: React.FC<PeopleDetailProps> = ({
  person,
  interactionToken,
  onPersonUpdated,
}) => {
  const [data, setData] =
    useState<Person | null>(person);

  const [saving, setSaving] =
    useState(false);

  /*
   * Address picker
   */
  const [addressPickerOpen, setAddressPickerOpen] =
    useState(false);

  const [pickedAddresses, setPickedAddresses] =
    useState<any[]>([]);


  /*
   * Keep local person data synchronised with the supplied person.
   */
  useEffect(() => {
    setData(person);

    const existingAddresses = (person as any)?.addresses;

    setPickedAddresses(
      Array.isArray(existingAddresses)
        ? existingAddresses
        : []
    );
  }, [person]);


  const handleUpdate = async (
    id: number,
    field: keyof Person,
    value: any
  ) => {
    if (!data?.id) {
      return;
    }

    setSaving(true);

    try {
      const payloadValue = Array.isArray(value)
        ? value
        : value;

      const payload = {
        [field]: payloadValue,
      } as Partial<Person>;

      const updated = interactionToken
        ? await peopleAPI.updateWithInteractionToken(
            id,
            payload,
            interactionToken
          )
        : await peopleAPI.update(id, payload);

      const updatedPerson: Person = {
        ...data,
        ...updated,
      };

      setData(updatedPerson);

      /*
       * If the API returned updated address objects,
       * keep the address display in sync too.
       */
      const returnedAddresses =
        (updatedPerson as any)?.addresses;

      if (Array.isArray(returnedAddresses)) {
        setPickedAddresses(returnedAddresses);
      }

      onPersonUpdated?.(updatedPerson);
    } catch (error: any) {
      console.error(
        "Update error:",
        field,
        error
      );

      alert(
        `Failed to update field "${String(field)}": ${
          error?.response?.data
            ? JSON.stringify(error.response.data)
            : error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };


  /*
   * Save selected addresses.
   *
   * The Person API expects the writable address_ids field,
   * while addresses contains the expanded address objects
   * used for displaying them.
   */
  const confirmAddresses = async () => {
    if (!data?.id) {
      return;
    }

    const addressIds = pickedAddresses
      .map((address) => address?.id)
      .filter(
        (id): id is number =>
          id !== undefined &&
          id !== null
      );

    try {
      await handleUpdate(
        data.id,
        "address_ids" as keyof Person,
        addressIds
      );

      /*
       * Keep the selected objects visible immediately,
       * even if the update endpoint only returns address_ids.
       */
      setData((previous) => {
        if (!previous) {
          return previous;
        }

        const updatedPerson = {
          ...previous,
          addresses: pickedAddresses,
        } as Person;

        onPersonUpdated?.(updatedPerson);

        return updatedPerson;
      });

      setAddressPickerOpen(false);
    } catch (error) {
      console.error(
        "Failed to update addresses:",
        error
      );
    }
  };


  if (!data) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">
          No person selected.
        </Typography>
      </Box>
    );
  }


  const tabs: GenericTabItem[] = [
    {
      id: "profile",
      label: "Profile",
      content: (
        <Grid2 container spacing={2}>
          {/* Personal details */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <EditableFieldsTable<Person>
              record={data}
              onSave={(field, value) =>
                handleUpdate(
                  data.id,
                  field,
                  value
                )
              }
              fields={[
                {
                  field: "gender",
                  label: "Gender",
                  type: "select",
                  placeholder: "Select gender",
                  options: [
                    {
                      value: "male",
                      label: "Male",
                    },
                    {
                      value: "female",
                      label: "Female",
                    },
                    {
                      value: "unsure",
                      label: "Unsure",
                    },
                    {
                      value: "other",
                      label: "Other",
                    },
                  ],
                },
                {
                  field: "date_of_birth",
                  label: "Date of Birth",
                  type: "date",
                },
                {
                  field: "age",
                  label: "Age",
                  type: "integer",
                  min: 0,
                  max: 130,
                },
                {
                  field: "relationship_status",
                  label: "Relationship Status",
                  type: "select",
                  placeholder:
                    "Select relationship status",
                  options: [
                    {
                      value: "single",
                      label: "Single",
                    },
                    {
                      value: "married",
                      label: "Married",
                    },
                    {
                      value: "divorced",
                      label: "Divorced",
                    },
                    {
                      value: "widowed",
                      label: "Widowed",
                    },
                    {
                      value: "separated",
                      label: "Separated",
                    },
                    {
                      value: "other",
                      label: "Other",
                    },
                  ],
                },
                {
                  field: "preferred_name",
                  label: "Preferred Name",
                  type: "text",
                },
              ]}
            />
          </Grid2>


          {/* Contact details */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <EditableFieldsTable<Person>
              record={data}
              onSave={(field, value) =>
                handleUpdate(
                  data.id,
                  field,
                  value
                )
              }
              fields={[
                {
                  field: "phone",
                  label: "Phone",
                },
                {
                  field: "email",
                  label: "Email",
                },
              ]}
            />


            {/* Address selection */}
            <Grid2
              container
              spacing={2}
              sx={{ mt: 1 }}
            >
              {/* Label + edit/add button */}
              <Grid2
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Addresses
                  </Typography>

                  <RoundIconButton
                    colour="clear"
                    displaySize="inline"
                    onClick={() =>
                      setAddressPickerOpen(true)
                    }
                    icon={
                      pickedAddresses.length > 0 ? (
                        <EditIcon fontSize="small" />
                      ) : (
                        <AddIcon fontSize="small" />
                      )
                    }
                  />
                </Stack>
              </Grid2>


              {/* Selected address list */}
              <Grid2
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                {pickedAddresses.length === 0 ||
                pickedAddresses[0] === "" ||
                pickedAddresses[0] === " " ? (
                  <Typography variant="body2">
                    No addresses listed
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {pickedAddresses.map(
                      (address: any) => (
                        <Stack
                          key={address.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Typography variant="body2">
                            {formatAddress(address)}
                          </Typography>

                          <RoundIconButton
                            colour="clear"
                            displaySize="inline"
                            icon={
                              <LaunchIcon fontSize="small" />
                            }
                            onClick={() =>
                              window.open(
                                `/community/addresses/${address?.id}/`,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          />
                        </Stack>
                      )
                    )}
                  </Box>
                )}
              </Grid2>
            </Grid2>


            {/* Address picker dialog */}
            <AddressPickerDialog
              open={addressPickerOpen}
              title="Choose Addresses"
              fetchAddresses={addressesAPI.getAll}
              selected={pickedAddresses}
              onSelectedChange={setPickedAddresses}
              disabledIds={[]}
              confirmLabel={"Save Addresses"}
              onClose={() => {
                const existing =
                  (data as any)?.addresses;

                setPickedAddresses(
                  Array.isArray(existing)
                    ? existing
                    : []
                );

                setAddressPickerOpen(false);
              }}
              onConfirm={confirmAddresses}
            />
          </Grid2>
        </Grid2>
      ),
    },

    {
      id: "religious-details",
      label: "Religion",
      content: (
        <>
          <ReligionMultiStageSelector
            label="Religion (Self Identified)"
            value={data.religion_self_identified}
            onChange={(ids: any) =>
              handleUpdate(
                data.id,
                "religion_self_identified_ids" as keyof Person,
                ids
              )
            }
          />

          <ReligionMultiStageSelector
            label="Religion (User Identified)"
            value={data.religion_user_identified}
            onChange={(ids: any) =>
              handleUpdate(
                data.id,
                "religion_user_identified_ids" as keyof Person,
                ids
              )
            }
          />

          <EditableFieldsTable<Person>
            record={data}
            onSave={(field, value) =>
              handleUpdate(
                data.id,
                field,
                value
              )
            }
            fields={[
              {
                field: "place_of_worship",
                label: "Place of Worship",
              },
              {
                field: "baptized",
                label: "Baptized",
                type: "boolean",
              },
              {
                field: "baptism_date",
                label: "Baptism Date",
                type: "date",
              },
              {
                field: "confirmation_date",
                label: "Confirmation Date",
                type: "date",
              },
              {
                field: "first_communion_date",
                label: "First Communion Date",
                type: "date",
              },
            ]}
          />
        </>
      ),
    },
  ];


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: {
          xs: 3,
          md: 4,
        },
      }}
    >
      <InlineSummary<Person>
        record={data}
        titleFields={[
          "first_name",
          "last_name",
        ]}
        descriptionField="preferred_name"
        onUpdate={handleUpdate}
        loading={saving}
      />

      <PersonDiscipleshipStageCard
        person={data}
        interactionToken={interactionToken}
        includeLinkToDiscipleshipPathway={false}
        onPersonUpdated={(updated) => {
          setData(updated);

          const addresses =
            (updated as any)?.addresses;

          if (Array.isArray(addresses)) {
            setPickedAddresses(addresses);
          }

          onPersonUpdated?.(updated);
        }}
      />

      <GenericTabs
        tabs={tabs}
        contentPadding={3}
        keepMounted={false}
      />
    </Box>
  );
};

export default PeopleDetail;